import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Phone, Mail, Globe, Star, CheckCircle, Loader2, ArrowLeft,
  Users, Award, 
  GraduationCap, Heart, Sparkles, MapPin
} from 'lucide-react';
import SEO from '../components/SEO';
import CourseCards from '../components/CourseCards';
import HowToReach from '../components/HowToReach';
import { schoolService, School } from '../services/schoolService';
import { courseService, Course } from '../services/courseService';
import { mediaService } from '../services/mediaService';

const SchoolDetails = () => {
  const { slug } = useParams<{ slug: string }>();
  const [school, setSchool] = useState<School | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [error, setError] = useState('');
  const [schoolMediaUrls, setSchoolMediaUrls] = useState<{
    images: { logo?: string; coverImage?: string; gallery: string[] };
    videos: { introVideo?: string; gallery: string[] };
  }>({ images: { gallery: [] }, videos: { gallery: [] } });

  useEffect(() => {
    if (slug) {
      fetchSchoolDetails();
    }
  }, [slug]);

  useEffect(() => {
    if (school?._id) {
      fetchCourses();
    }
  }, [school?._id]);

  const fetchSchoolDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await schoolService.getSchoolById(slug!);
      if (response.success && response.data) {
        const schoolData = (response.data as any).school || response.data;
        setSchool(schoolData);
        
        // Build display URLs from keys (domain + key)
        const getUrl = (key: string | undefined) => (key ? mediaService.getMediaUrlFromKey(key) : undefined);
        setSchoolMediaUrls({
          images: {
            logo: getUrl(schoolData.images?.logo),
            coverImage: getUrl(schoolData.images?.coverImage),
            gallery: (schoolData.images?.gallery || []).map(getUrl).filter(Boolean) as string[],
          },
          videos: {
            introVideo: getUrl(schoolData.videos?.introVideo),
            gallery: (schoolData.videos?.gallery || []).map(getUrl).filter(Boolean) as string[],
          },
        });
      } else {
        setError('School not found');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load school details.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    if (!school?._id) return;
    setLoadingCourses(true);
    try {
      const response = await courseService.getSchoolCourses(school._id, 1, 100);
      if (response.success && response.data) {
        const coursesData = response.data.courses || [];
        
        // Convert course media fileKeys to signed URLs
        const coursesWithUrls = await Promise.all(
          coursesData.map(async (course: Course) => {
            if (course.media) {
              const convertedMedia = await mediaService.convertMediaToUrls(course.media);
              return {
                ...course,
                media: {
                  images: convertedMedia.images || [],
                  videos: convertedMedia.videos || [],
                },
              };
            }
            return course;
          })
        );
        
        setCourses(coursesWithUrls);
      }
    } catch (err: any) {
      console.error('Failed to fetch courses:', err);
    } finally {
      setLoadingCourses(false);
    }
  };



  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !school) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-5 font-semibold text-ivory-900 mb-4">School Not Found</h2>
          <p className="text-ivory-600 mb-6">{error || 'The school you are looking for does not exist.'}</p>
          <Link to="/" className="text-primary hover:text-primary-dark font-semibold">
            ← Back to Listings
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${school.name} - Book My Yoga TTC`}
        description={school.shortDescription || school.description}
        url={`https://bookmyyogattc.com/schools/${school.slug || school._id}`}
      />
      <div className="pt-20 min-h-screen bg-white">
          {/* Back Button */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-ivory-700 hover:text-primary mb-4 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Listings
          </Link>
        </div>

        {/* Media Collage Section */}
        <section className="relative w-full overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* School Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
              className="mb-6"
          >
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              {schoolMediaUrls.images.logo && (
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-xl overflow-hidden bg-ivory-100 shadow-lg flex-shrink-0">
                  <img 
                    src={schoolMediaUrls.images.logo} 
                    alt={school.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      e.stopPropagation();
                    }}
                  />
                </div>
              )}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h1 className="text-5 md:text-5 font-semibold text-ivory-900">{school.name}</h1>
                  {school.isFeatured && (
                    <div className="flex items-center gap-1 bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-2 font-semibold">
                        <Star className="h-4 w-4 fill-yellow-400" />
                      <span>Featured</span>
                    </div>
                  )}
                </div>
                  <div className="flex flex-wrap items-center gap-3 text-ivory-600">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    <span className="font-medium">{school.location}, {school.city}, {school.country}</span>
                  </div>
                  {school.rating > 0 && (
                    <div className="flex items-center gap-1">
                      <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                      <span className="font-semibold">{school.rating.toFixed(1)}</span>
                      <span className="text-2">({school.totalReviews} reviews)</span>
                    </div>
                  )}
                </div>
                  </div>
              </div>
          </motion.div>

            {/* Media Collage Grid */}
            {(schoolMediaUrls.images.gallery && schoolMediaUrls.images.gallery.length > 0) || 
             (schoolMediaUrls.videos.gallery && schoolMediaUrls.videos.gallery.length > 0) || 
             schoolMediaUrls.images.coverImage || 
             schoolMediaUrls.videos.introVideo ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {/* Cover Image - Large Featured */}
                {schoolMediaUrls.images.coverImage && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="col-span-2 row-span-2 aspect-square md:aspect-auto md:h-full rounded-xl overflow-hidden group cursor-pointer relative shadow-lg"
                    onClick={() => schoolMediaUrls.images.coverImage && window.open(schoolMediaUrls.images.coverImage, '_blank')}
                  >
                    <img 
                      src={schoolMediaUrls.images.coverImage} 
                      alt={`${school.name} - Cover`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        e.stopPropagation();
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                      <span className="text-white font-semibold">Cover Image</span>
            </div>
          </motion.div>
              )}

                {/* Intro Video - Medium Featured */}
                {schoolMediaUrls.videos.introVideo && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="col-span-2 aspect-video rounded-xl overflow-hidden group cursor-pointer relative shadow-lg"
                    onClick={() => schoolMediaUrls.videos.introVideo && window.open(schoolMediaUrls.videos.introVideo, '_blank')}
                  >
                    <video 
                      src={schoolMediaUrls.videos.introVideo}
                      className="w-full h-full object-cover"
                      muted
                      loop
                      playsInline
                      onError={(e) => {
                        const target = e.target as HTMLVideoElement;
                        target.style.display = 'none';
                        e.stopPropagation();
                      }}
                    />
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                      <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <svg className="w-8 h-8 text-primary ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                  </div>
                  </div>
                </motion.div>
              )}

                {/* Gallery Images - Small Grid Items */}
                {schoolMediaUrls.images.gallery && schoolMediaUrls.images.gallery.length > 0 && schoolMediaUrls.images.gallery.slice(0, 4).map((image, index) => (
              <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                    className="aspect-square rounded-xl overflow-hidden group cursor-pointer relative shadow-md"
                    onClick={() => window.open(image, '_blank')}
                  >
                    <img 
                      src={image} 
                      alt={`${school.name} - Gallery ${index + 1}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        // Prevent infinite loops by hiding broken images
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        e.stopPropagation();
                        // Prevent default error behavior
                        e.preventDefault();
                      }}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                  </motion.div>
                ))}

                {/* Video Gallery - Small Grid Items */}
                {schoolMediaUrls.videos.gallery && schoolMediaUrls.videos.gallery.length > 0 && schoolMediaUrls.videos.gallery.slice(0, 2).map((video, index) => (
                  <motion.div
                    key={`video-${index}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    className="aspect-square rounded-xl overflow-hidden group cursor-pointer relative shadow-md"
                    onClick={() => window.open(video, '_blank')}
                  >
                    <video 
                      src={video}
                      className="w-full h-full object-cover"
                      muted
                      loop
                      playsInline
                      onError={(e) => {
                        const target = e.target as HTMLVideoElement;
                        target.style.display = 'none';
                        e.stopPropagation();
                      }}
                    />
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                      <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <svg className="w-6 h-6 text-primary ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                  </div>
                                    </div>
                  </motion.div>
                ))}
                  </div>
                ) : (
              <div className="bg-ivory-50 rounded-xl p-8 text-center">
                <p className="text-ivory-600">No media available for this school.</p>
                            </div>
                            )}
                          </div>
        </section>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* About School Section */}
          <section className="mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-5 md:text-5 font-semibold text-ivory-900 mb-4 tracking-wide">
                ABOUT {school.name.toUpperCase()}
              </h2>
              <div className="text-ivory-700 leading-relaxed space-y-3">
                {school.description ? (
                  <p className="whitespace-pre-wrap text-3">{school.description}</p>
                ) : (
                  <p className="text-3">{school.shortDescription || 'A renowned yoga school dedicated to providing authentic and transformative yoga teacher training experiences.'}</p>
                              )}
                            </div>
            </motion.div>
          </section>

          {/* School Highlights Section */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-5 md:text-5 font-semibold text-ivory-900 mb-6 tracking-wide">
                SCHOOL HIGHLIGHTS
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: school.yogaAllianceCertified ? Award : Globe, text: school.yogaAllianceCertified ? 'Yoga Alliance Certified' : 'Certified School', color: 'bg-blue-100 text-blue-600' },
                  { icon: GraduationCap, text: 'Experienced Teachers', color: 'bg-purple-100 text-purple-600' },
                  { icon: Heart, text: 'Authentic Practice', color: 'bg-pink-100 text-pink-600' },
                  { icon: Users, text: 'Community Focused', color: 'bg-green-100 text-green-600' },
                  { icon: Sparkles, text: 'Transformative Experience', color: 'bg-yellow-100 text-yellow-600' },
                  { icon: Star, text: `Rated ${school.rating > 0 ? school.rating.toFixed(1) : '5.0'}`, color: 'bg-primary/10 text-primary' }
                ].map((highlight, index) => (
                  <div key={index} className="text-center">
                    <div className={`w-12 h-12 ${highlight.color} rounded-full flex items-center justify-center mx-auto mb-2`}>
                      <highlight.icon className="h-6 w-6" />
                                    </div>
                    <p className="text-1 font-medium text-ivory-700">{highlight.text}</p>
                                    </div>
                                        ))}
                                      </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="text-5 md:text-5 font-semibold text-ivory-900 mb-6 tracking-wide">
                WHY CHOOSE THIS SCHOOL
              </h2>
              <ul className="space-y-3">
                {[
                  'Authentic yoga tradition and philosophy',
                  'Experienced and certified instructors',
                  'Beautiful and serene learning environment',
                  'Comprehensive curriculum and support',
                  'Strong community and networking opportunities',
                  'Post-training support and guidance'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-2">
                                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-ivory-700 text-3">{item}</span>
                                  </li>
                                ))}
                              </ul>
            </motion.div>
          </section>

          {/* Offered Courses Section */}
          <section className="mb-12" id="courses-section">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-6"
            >
              <h2 className="text-5 md:text-5 font-semibold text-ivory-900 mb-3 text-center tracking-wide">
                OFFERED COURSES
              </h2>
              <p className="text-center text-ivory-600 max-w-2xl mx-auto text-2">
                Explore our comprehensive yoga teacher training programs designed to deepen your practice and prepare you for teaching.
              </p>
            </motion.div>
            
            <CourseCards 
              courses={courses}
              schoolSlug={slug || school?._id || ''}
              schoolLocation={school?.location}
              schoolCity={school?.city}
              schoolImages={school?.images}
              loading={loadingCourses}
            />
            
            {courses.length > 0 && (
              <div className="mt-6 text-center">
                <p className="text-1 text-ivory-600">
                  <strong>Flexible payment plans available.</strong> Contact us for a Course Advisor.
                </p>
              </div>
            )}

            {/* How to Reach - Below Courses */}
            {(school as any).howToReach?.stations?.length > 0 && (
              <div className="mt-12">
                <HowToReach
                  stations={(school as any).howToReach.stations}
                  title="How to Reach This School"
                />
              </div>
            )}
          </section>

          {/* Student Testimonials Section */}
          {school.totalReviews > 0 && (
            <section className="mb-12">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-5 md:text-5 font-semibold text-ivory-900 mb-6 text-center tracking-wide"
              >
                STUDENT TESTIMONIALS
              </motion.h2>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-ivory-50 rounded-2xl p-6 md:p-8 border-2 border-primary/20 max-w-4xl mx-auto"
              >
                <div className="space-y-3">
                  <div className="text-5 text-primary/30 font-serif leading-none">"</div>
                  <p className="text-3 text-ivory-700 italic leading-relaxed">
                    This school provided an incredible learning experience. The teachers are knowledgeable, the environment is supportive, and I feel completely prepared to teach yoga.
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    <span className="font-semibold text-ivory-900 text-2">{school.rating.toFixed(1)}</span>
                    <span className="text-ivory-600 text-2">({school.totalReviews} reviews)</span>
                        </div>
                      </div>
              </motion.div>
            </section>
          )}

          {/* Intro Video */}
          {schoolMediaUrls.videos.introVideo && (
            <section className="mb-12">
                <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                  className="bg-white rounded-2xl shadow-lg border border-ivory-200 p-4 md:p-6"
                >
                <h2 className="text-5 md:text-5 font-semibold text-ivory-900 mb-4 text-center tracking-wide">
                  INTRODUCTION VIDEO
                  </h2>
                <div className="aspect-video rounded-xl overflow-hidden bg-ivory-100 max-w-4xl mx-auto">
                  <video 
                    src={schoolMediaUrls.videos.introVideo}
                    controls
                    className="w-full h-full object-cover"
                    preload="metadata"
                    onError={(e) => {
                      const target = e.target as HTMLVideoElement;
                      target.style.display = 'none';
                      e.stopPropagation();
                    }}
                  />
                </div>
              </motion.div>
            </section>
          )}

          {/* Image Gallery */}
              {schoolMediaUrls.images.gallery && schoolMediaUrls.images.gallery.length > 0 && (
            <section className="mb-12">
              <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-5 md:text-5 font-semibold text-ivory-900 mb-6 text-center tracking-wide"
              >
                IMAGE GALLERY
              </motion.h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {schoolMediaUrls.images.gallery.map((image, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="aspect-square rounded-xl overflow-hidden group cursor-pointer relative"
                    onClick={() => window.open(image, '_blank')}
                  >
                        <img 
                          src={image} 
                          alt={`${school.name} - Image ${index + 1}`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          loading="lazy"
                          onError={(e) => {
                            // Prevent infinite loops by hiding broken images
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                  </motion.div>
                    ))}
                  </div>
            </section>
              )}

          {/* Video Gallery */}
              {schoolMediaUrls.videos.gallery && schoolMediaUrls.videos.gallery.length > 0 && (
            <section className="mb-12">
              <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-5 md:text-5 font-semibold text-ivory-900 mb-6 text-center tracking-wide"
              >
                VIDEO GALLERY
              </motion.h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {schoolMediaUrls.videos.gallery.map((video, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="aspect-video rounded-xl overflow-hidden bg-ivory-100 shadow-lg"
                  >
                        <video 
                          src={video}
                          controls
                          className="w-full h-full object-cover"
                          preload="metadata"
                          onError={(e) => {
                            const target = e.target as HTMLVideoElement;
                            target.style.display = 'none';
                            e.stopPropagation();
                          }}
                        />
                  </motion.div>
                    ))}
                  </div>
            </section>
              )}

          {/* How to Reach - School Details Section */}
          {(school as any).howToReach?.stations?.length > 0 && (
            <section className="mb-12">
              <HowToReach
                stations={(school as any).howToReach.stations}
                title="How to Reach"
              />
            </section>
          )}

          {/* Contact & Additional Info Section */}
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
              {/* Contact Information */}
              <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-xl shadow-lg border border-ivory-200 p-4"
              >
                <h3 className="text-4 font-semibold text-ivory-900 mb-3">Contact Information</h3>
                <div className="space-y-3">
                  {school.phone && (
                    <div className="flex items-center gap-3 text-ivory-700">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Phone className="h-5 w-5 text-primary" />
                      </div>
                      <a href={`tel:${school.phone}`} className="hover:text-primary transition-colors font-medium">
                        {school.phone}
                      </a>
                    </div>
                  )}
                  {school.email && (
                    <div className="flex items-center gap-3 text-ivory-700">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Mail className="h-5 w-5 text-primary" />
                      </div>
                      <a href={`mailto:${school.email}`} className="hover:text-primary transition-colors font-medium break-all">
                        {school.email}
                      </a>
                    </div>
                  )}
                  {school.website && (
                    <div className="flex items-center gap-3 text-ivory-700">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Globe className="h-5 w-5 text-primary" />
                      </div>
                      <a 
                        href={school.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-primary transition-colors font-medium break-all"
                      >
                        Visit Website
                      </a>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Programs Offered */}
              {school.programs && (
                <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-white rounded-xl shadow-lg border border-ivory-200 p-4"
                >
                  <h3 className="text-4 font-semibold text-ivory-900 mb-3">Programs Offered</h3>
                  <div className="space-y-2">
                    {school.programs.teacherTraining && (
                      <div className="flex items-center gap-2 text-ivory-700">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        <span className="font-medium text-2">Teacher Training</span>
                      </div>
                    )}
                    {school.programs.workshops && (
                      <div className="flex items-center gap-2 text-ivory-700">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        <span className="font-medium text-2">Workshops</span>
                      </div>
                    )}
                    {school.programs.retreats && (
                      <div className="flex items-center gap-2 text-ivory-700">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        <span className="font-medium text-2">Retreats</span>
                      </div>
                    )}
                    {school.programs.onlineClasses && (
                      <div className="flex items-center gap-2 text-ivory-700">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        <span className="font-medium text-2">Online Classes</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

            {/* Facilities & Amenities */}
                <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-xl shadow-lg border border-ivory-200 p-4"
                >
              <h3 className="text-4 font-semibold text-ivory-900 mb-3">Facilities & Amenities</h3>
                  <div className="space-y-1.5">
                {school.facilities && school.facilities.slice(0, 5).map((facility, index) => (
                      <div key={index} className="flex items-center gap-2 text-ivory-700">
                        <CheckCircle className="h-4 w-4 text-primary" />
                    <span className="text-2">{facility}</span>
                      </div>
                    ))}
                {school.amenities && school.amenities.slice(0, 5).map((amenity, index) => (
                      <div key={index} className="flex items-center gap-2 text-ivory-700">
                        <CheckCircle className="h-4 w-4 text-primary" />
                    <span className="text-2">{amenity}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
          </section>
        </div>
      </div>

    </>
  );
};

export default SchoolDetails;

